/**
 * Vista 15 · Ítems de Selección.
 *
 * Configura el catálogo de la sección 1 del expediente, que el prototipo tenía
 * cableado en el HTML (`P-17`). Es la lista de lo que Fiscalización comprueba
 * por sí misma, sin preguntarle a nadie (`NEG-1`), y cambia entre convocatorias.
 *
 * Los cambios aplican a expedientes que se creen **desde ahora**: los del
 * bloque vigente conservan los suyos, porque el trabajo ya hecho sobre ellos
 * no puede quedar huérfano de su ítem.
 */
import { useState } from 'react'
import { Button, Card, CardContent, Input } from '@mpfn/ui'
import { Callout, FormField, RepeatableRows } from '@mpfn/patterns'
import { useGuardarItemsSeleccion, useItemsSeleccion } from '../api/consultas.js'
import type { ItemSeleccionCatalogo } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { Interruptor } from '../componentes/Conmutadores.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'

/** Código en minúsculas y guiones bajos, igual que valida el servidor. */
const CODIGO_VALIDO = /^[a-z][a-z0-9_]{2,39}$/

export function ItemsSeleccion() {
  const items = useItemsSeleccion()

  return (
    <Pagina ancho="formulario"
      titulo="Ítems de Selección"
      descripcion="Lo que Fiscalización comprueba por sí misma en la sección 1 del expediente."
    >
      <Datos estado={items}>{(lista) => <Editor iniciales={lista} />}</Datos>
    </Pagina>
  )
}

function Editor({ iniciales }: { iniciales: readonly ItemSeleccionCatalogo[] }) {
  const guardar = useGuardarItemsSeleccion()
  const [items, setItems] = useState<readonly ItemSeleccionCatalogo[]>(iniciales)

  function modificar(indice: number, cambios: Partial<ItemSeleccionCatalogo>): void {
    setItems((previo) => previo.map((item, posicion) => (posicion === indice ? { ...item, ...cambios } : item)))
  }

  const invalidos = items.filter(
    (item) => !CODIGO_VALIDO.test(item.codigo) || item.denominacion.trim() === '',
  )
  const duplicados = new Set(items.map((item) => item.codigo)).size !== items.length

  return (
    <>
      <Callout tone="info" title="Aplican a los expedientes que se creen desde ahora">
        Los expedientes del bloque vigente conservan los ítems con los que nacieron, junto con lo
        que ya se verificó sobre ellos.
      </Callout>

      <Card>
        <CardContent>
          <RepeatableRows
            items={items}
            rowKey={(item, indice) => `${item.codigo}-${String(indice)}`}
            addLabel="Agregar ítem"
            empty="Todavía no hay ítems configurados."
            onAdd={() => {
              setItems((previo) => [
                ...previo,
                {
                  codigo: '',
                  orden: previo.length + 1,
                  denominacion: '',
                  exigeEvidencia: false,
                  activo: true,
                },
              ])
            }}
            onRemove={(indice) => {
              setItems((previo) => previo.filter((_, posicion) => posicion !== indice))
            }}
            renderRow={(item, indice) => (
              <div className="sigfisc-fila-campos sigfisc-fila-campos--item">
                <FormField
                  id={`item-orden-${String(indice)}`}
                  label="Orden"
                  required
                >
                  <Input
                    type="number"
                    min={1}
                    value={String(item.orden)}
                    onChange={(evento) => {
                      modificar(indice, { orden: Number(evento.target.value) })
                    }}
                  />
                </FormField>

                <FormField
                  id={`item-codigo-${String(indice)}`}
                  label="Código"
                  required
                  hint="Minúsculas y guiones bajos."
                  {...(item.codigo !== '' && !CODIGO_VALIDO.test(item.codigo)
                    ? { error: 'Use minúsculas, dígitos y guiones bajos, entre 3 y 40 caracteres.' }
                    : {})}
                >
                  <Input
                    value={item.codigo}
                    onChange={(evento) => {
                      modificar(indice, { codigo: evento.target.value })
                    }}
                  />
                </FormField>

                <FormField
                  id={`item-denominacion-${String(indice)}`}
                  label="Denominación"
                  required
                >
                  <Input
                    value={item.denominacion}
                    onChange={(evento) => {
                      modificar(indice, { denominacion: evento.target.value })
                    }}
                  />
                </FormField>

                <Interruptor
                  etiqueta="Exige evidencia"
                  marcado={item.exigeEvidencia}
                  onCambio={(valor) => {
                    modificar(indice, { exigeEvidencia: valor })
                  }}
                />

                <Interruptor
                  etiqueta="Activo"
                  marcado={item.activo}
                  onCambio={(valor) => {
                    modificar(indice, { activo: valor })
                  }}
                />
              </div>
            )}
          />
        </CardContent>
      </Card>

      {duplicados && (
        <Callout tone="critical" title="Hay códigos repetidos">
          Cada ítem necesita un código distinto.
        </Callout>
      )}

      <Button
        variant="primary"
        loading={guardar.isPending}
        disabled={invalidos.length > 0 || duplicados}
        onClick={() => {
          guardar.mutate(items, {
            onSuccess: (datos) => {
              avisarExito(`Se guardaron ${plural(datos.items.length, 'ítem', 'ítems')}.`)
            },
            onError: (error) => {
              avisarError(error, 'No se pudieron guardar los ítems')
            },
          })
        }}
      >
        Guardar los ítems
      </Button>
    </>
  )
}
